import { Dialog } from "primereact/dialog"
import { useCallback, useEffect, useState } from "react";
import { useAddPlan, useGetExercisesListMutation } from "../../hooks/useGetDataQuery";
import "./addExercisesDialog.css";
import PatientCodeDialog from "../PatientCodeDialog/patientCodeDialog";
import { useToastContext } from "../../Lib/Contexts/toastContext";
import ExerciseVideoDialog from "../../Library/exerciseVideoDialog";
import AddExercisesPanelsSection from "./Components/addExercisesPanelsSection";
import AddExercisesWizardSection from "./Components/addExercisesWizardSection";
import { purifyData } from "../purifyExerciseMethods";
import { mappedDays } from "../exerciseDefaults";

const PAGE_SIZE = 200;

const AddExercisesDialog = ({ t, exerciseDialogVisible, setExerciseDialogVisible, selectedPatient, setSelectedPatient }: any) => {
  const [exerciseVideoDialogVisible, setExerciseVideoDialogVisible] = useState<boolean>(false);
  const [isPanelsSectionSelected, setIsPanelsSectionSelected] = useState<boolean>(true);
  const selectedPatientPhoneNumber = selectedPatient?.phoneNumber || "";
  const [lazyParamsForExercises, setLazyParamsForExercises] = useState<any>({
    rows: PAGE_SIZE,
    page: 0,
    first: 0,
    filters: {
      name: {
        matchMode: "contains",
        value: "",
      },
    },
  });

  const {
    data: exercisesData,
    isSuccess: isSuccessExercisesData,
    mutate: getExercisesList,
  } = useGetExercisesListMutation();

  const [exercises, setExercises] = useState<any[]>([]);
  const [selectedExercises, setSelectedExercises] = useState<any[]>([]);

  const [carePlanName, setCarePlanName] = useState<string>("");
  const [dateOfStart, setDateOfStart] = useState<Date>(new Date());
  const [dateOfEnd, setDateOfEnd] = useState<Date>(() => { return new Date(new Date().setDate(new Date().getDate() + 14)) });

  const [codeDialogVisible, setCodeDialogVisible] = useState<boolean>(false);

  const [selectedExerciseForVideo, setSelectedExerciseForVideo] = useState<any>();

  const { showToast }: any = useToastContext();

  const {
    mutate: addPlanMutation,
    data: addPlanData,
    isSuccess: isAddPlanSuccess,
    isError: isAddPlanError,
    isPending: isPendingAddPlan,
  } = useAddPlan();

  useEffect(() => {
    let isMounted = true;

    if (isAddPlanSuccess) {
      if (isMounted) {
        showToast({
          severity: "success",
          summary: "Success",
          detail: "Exercises saved successfully!",
        });

        const { isFirstPlan } = addPlanData;
        if (isFirstPlan && isMounted) {
          setCodeDialogVisible(true);
        }
        onHideDialog();
      }
    }

    return () => {
      isMounted = false;
    }
  }, [isAddPlanSuccess]);

  useEffect(() => {
    if (isAddPlanError) {
      showError("exercisesCouldNotBeSaved");
    }
  }, [isAddPlanError]);

  useEffect(() => {
    if (isSuccessExercisesData) {
      const purifiedExercisesData = purifyData(exercisesData?.data)

      setExercises(purifiedExercisesData)
    }
  }, [exercisesData, isSuccessExercisesData]);

  useEffect(() => {
    if (lazyParamsForExercises) {
      getExercisesList(lazyParamsForExercises);
    }
  }, [lazyParamsForExercises])

  const sortExercisesBySelected = (
    exercises: any[],
    selectedExercises: any[]
  ): any[] => {
    const selectedEx = new Set(selectedExercises.map(sel => sel.exercise));

    return [...exercises].sort((a, b) => {
      const isASelected = selectedEx.has(a.exercise);
      const isBSelected = selectedEx.has(b.exercise);

      if (isASelected && !isBSelected) return -1;
      if (!isASelected && isBSelected) return 1;
      return 0;
    });
  };

  useEffect(() => {
    if (selectedExercises.length > 0) {
      const sorted = sortExercisesBySelected(exercises, selectedExercises);
      setExercises(sorted);
    }
  }, [selectedExercises]);

  const clearAll = () => {
    setSelectedExercises([]);
    setCarePlanName("");
    setDateOfStart(new Date());
    setDateOfEnd(new Date(new Date().setDate(new Date().getDate() + 14)));
  }

  const handleSelectButton = () => {
    clearAll();
    setIsPanelsSectionSelected(prev => !prev)
  }

  const onHide = () => {
    clearAll();
    setExercises([]);
    setLazyParamsForExercises({
      rows: PAGE_SIZE,
      page: 0,
      first: 0,
      filters: {
        name: {
          matchMode: "contains",
          value: "",
        },
      },
    });
  }

  const onHideDialog = useCallback(() => {
    if (exerciseDialogVisible) {
      setExerciseDialogVisible(false);
      setIsPanelsSectionSelected(true);
    }
  }, [exerciseDialogVisible]);

  const onCodeDialogHide = () => {
    onHide();
    localStorage.setItem("patient", JSON.stringify(selectedPatient));
    setCodeDialogVisible(false);
  }

  const handleDelete = (exercise: any) => {
    const filteredExercises = selectedExercises.filter(item => item.exercise.id !== exercise.exercise.id);
    if (filteredExercises.length === 0) {
      setCarePlanName("");
    }
    setSelectedExercises(filteredExercises);
  }

  const handleCarePlanName = ({ target: { value } }: any) => {
    if (value.length <= 50) {
      setCarePlanName(value);
      return;
    }
  }

  const showError = (message: string) => {
    showToast({
      severity: "error",
      summary: "Error",
      detail: t(message),
    })
  }

  const normalizeDate = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const validatePlanParameters = () => {
    if (!carePlanName || carePlanName.trim() === "") {
      showError("carePlanNameEmptyError");
      return false;
    }

    if (carePlanName.length < 2 || carePlanName.length > 50) {
      showError("carePlanNameBetween2and50");
      return false;
    }

    if (!dateOfStart || isNaN(dateOfStart.getTime())) {
      showError("startDateEmptyError");
      return false;
    }

    const now = new Date();
    const tenMinutesAgo = new Date(now.getTime() - 10 * 60 * 1000);

    if (dateOfStart.getTime() < tenMinutesAgo.getTime()) {
      showError("startDateCannotBeEarlierThanNow");
      return false;
    }

    if (!dateOfEnd || isNaN(dateOfEnd.getTime())) {
      showError("endDateEmptyError");
      return false;
    }

    const normalizedDateofEnd = normalizeDate(dateOfEnd);
    const normalizedNow = normalizeDate(now);
    if (normalizedDateofEnd.getTime() < normalizedNow.getTime()) {
      showError("endDateCannotBeEarlierThanNow");
      return false;
    }

    if (!selectedExercises || selectedExercises?.length === 0) {
      showError("selectedExercisesEmptyError");
      return false;
    }

    if (selectedExercises.length > 15) {
      showError("selectedExercisesLimitExceededError");
      return false;
    }

    return true;
  }

  const handleApprovePrescription = () => {
    if (!validatePlanParameters()) {
      return;
    }

    const refactoredExercises = selectedExercises.map((exercise: any) => {
      if (!exercise.isEveryOtherDay) {
        return {
          exerciseProcedureId: exercise.exercise.id,
          setCount: exercise.set,
          repeatCount: exercise.repeat,
          tolerance: exercise.tolerance,
          restInterval: exercise.restInterval * 3600,
          days: exercise.days.map((day: string) => mappedDays[day]),
          isEveryOtherDay: false,
        };
      }
      return {
        exerciseProcedureId: exercise.exercise.id,
        setCount: exercise.set,
        repeatCount: exercise.repeat,
        tolerance: exercise.tolerance,
        restInterval: exercise.restInterval * 3600,
        isEveryOtherDay: true,
        days: [],
        everyOtherDayPeriod: exercise.everyOtherDayPeriod,
      };
    })

    addPlanMutation({
      name: carePlanName,
      exerciseList: refactoredExercises,
      status: "NotStarted",
      isActive: true,
      patientId: selectedPatient.id,
      phoneNumber: selectedPatientPhoneNumber,
      planDateStart: new Date(new Date(dateOfStart).getTime() + 3 * 60 * 60 * 1000),
      planDateEnd: new Date(new Date(dateOfEnd).getTime() + 3 * 60 * 60 * 1000),
    });
  }

  const handleDateOfStart = ({ value }: any) => {
    setDateOfStart(value);
  }

  const handleDateOfEnd = ({ value }: any) => {
    setDateOfEnd(value);
  }

  useEffect(() => {
    exerciseDialogVisible && onHide();
  }, [exerciseDialogVisible]);

  const renderSection = () => {
    switch (isPanelsSectionSelected) {
      case true:
        return <AddExercisesPanelsSection
          t={t}
          isPanelsSectionSelected={isPanelsSectionSelected}
          selectedPatient={selectedPatient}
          onHideDialog={onHideDialog}
          selectedExercises={selectedExercises}
          setSelectedExercises={setSelectedExercises}
          handleDelete={handleDelete}
          carePlanName={carePlanName}
          setCarePlanName={setCarePlanName}
          handleCarePlanName={handleCarePlanName}
          dateOfStart={dateOfStart}
          handleDateOfStart={handleDateOfStart}
          dateOfEnd={dateOfEnd}
          handleDateOfEnd={handleDateOfEnd}
          isPendingAddPlan={isPendingAddPlan}
          handleSelectButton={handleSelectButton}
          handleApprovePrescription={handleApprovePrescription}
          handleDeleteAll={clearAll}
          setExerciseVideoDialogVisible={setExerciseVideoDialogVisible}
          setSelectedExerciseForVideo={setSelectedExerciseForVideo}
        />
      default:
        return <AddExercisesWizardSection
          t={t}
          isPanelsSectionSelected={isPanelsSectionSelected}
          handleSelectButton={handleSelectButton}
          onHideDialog={onHideDialog}
          selectedPatient={selectedPatient}
          handleCarePlanName={handleCarePlanName}
          selectedExercises={selectedExercises}
          setSelectedExercises={setSelectedExercises}
          carePlanName={carePlanName}
          handleDelete={handleDelete}
          setCarePlanName={setCarePlanName}
          dateOfStart={dateOfStart}
          dateOfEnd={dateOfEnd}
          handleDateOfStart={handleDateOfStart}
          handleDateOfEnd={handleDateOfEnd}
          isPendingAddPlan={isPendingAddPlan}
          handleApprovePrescription={handleApprovePrescription}
          setExerciseVideoDialogVisible={setExerciseVideoDialogVisible}
          setSelectedExerciseForVideo={setSelectedExerciseForVideo}
        />
    }
  }

  return (
    <>
      <Dialog showHeader={false} className="exercises-dialog" visible={exerciseDialogVisible} blockScroll style={{ width: "95vw", height: "100vh", borderRadius: '24px', overflow: "hidden" }} onHide={onHideDialog}>
        {renderSection()}
      </Dialog>
      <ExerciseVideoDialog exerciseVideoDialogVisible={exerciseVideoDialogVisible} setExerciseVideoDialogVisible={setExerciseVideoDialogVisible} selectedExercise={selectedExerciseForVideo} />
      <PatientCodeDialog t={t} codeDialogVisible={codeDialogVisible} selecetedPatient={selectedPatient} carePlanName={carePlanName} selectedExercises={selectedExercises} onCodeDialogHide={onCodeDialogHide} setSelectedPatient={setSelectedPatient} />
    </>
  )
}

export default AddExercisesDialog;